/**
 * WASL Email Service
 * 
 * This service provides email functionality for the WASL platform.
 * It calls a backend email API that uses Gmail SMTP.
 * 
 * Usage:
 *   import { sendEmail, sendOrderConfirmation, sendWelcomeEmail } from './services/emailService';
 *   
 *   // Send a custom email
 *   await sendEmail({
 *     to: 'user@example.com',
 *     subject: 'Welcome to WASL',
 *     body: 'Thank you for joining us!'
 *   });
 * 
 *   // Use pre-built templates
 *   await sendOrderConfirmation({ orderId: '12345', customerEmail: 'user@example.com' });
 */

// Email API endpoint - Update this to your backend email endpoint
// In production, use HTTPS. For local development, HTTP is fine.
const getEmailEndpoint = () => {
  // If environment variable is set, use it (baked in at build time)
  if (process.env.REACT_APP_EMAIL_API_ENDPOINT) {
    return process.env.REACT_APP_EMAIL_API_ENDPOINT;
  }
  
  // If running in production (HTTPS), use HTTPS endpoint
  // This handles runtime detection for production builds
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    // Use HTTPS endpoint for production
    const hostname = window.location.hostname;
    if (hostname === 'wasl.om' || hostname.includes('wasl.om')) {
      return 'https://wasl.om/api/email/send-email';
    }
  }
  
  // Default to localhost for development
  return 'http://localhost:3001/api/send-email';
};

const EMAIL_API_ENDPOINT = getEmailEndpoint();

/**
 * Same URL the send-email route uses, with HTTPS / production host fixes applied at runtime.
 * Keeps forgot-password requests from targeting http://localhost on https://wasl.om (mixed content → "Failed to fetch").
 */
const getResolvedEmailApiUrl = () => {
  let endpoint = EMAIL_API_ENDPOINT;
  if (typeof window !== 'undefined') {
    const isHTTPS = window.location.protocol === 'https:';
    const isProduction =
      window.location.hostname === 'wasl.om' || window.location.hostname.includes('wasl.om');
    const origin = window.location.origin;
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const isPrivateNetworkHost =
      hostname.startsWith('192.168.') ||
      hostname.startsWith('10.') ||
      /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname);

    if (isHTTPS || isProduction) {
      if (endpoint.startsWith('http://wasl.om') || endpoint.startsWith('http://localhost')) {
        endpoint = endpoint.replace(/^http:\/\//, 'https://');
      } else if (endpoint.includes('wasl.om') && !endpoint.startsWith('https://')) {
        endpoint = endpoint.replace(/^http:\/\//, 'https://');
      }

      // If we are on HTTPS and the build-time endpoint points to localhost,
      // prefer the same-origin reverse-proxy path (works for wasl.om AND any deployed host).
      if (endpoint.includes('localhost')) {
        endpoint = `${origin}/api/email/send-email`;
      }

      // Back-compat: on wasl.om specifically, enforce the known production path.
      if (isProduction && endpoint.includes('/api/send-email')) {
        endpoint = 'https://wasl.om/api/email/send-email';
      }
    }

    // Local LAN testing (phone hitting http://192.168.x.x):
    // Our node email server listens on :3001 and exposes /api/send-email (NOT /api/email/send-email).
    if (isPrivateNetworkHost) {
      if (endpoint.includes('/api/email/send-email')) {
        endpoint = `${protocol}//${hostname}:3001/api/send-email`;
      } else if (endpoint.includes('localhost:3001')) {
        endpoint = `${protocol}//${hostname}:3001/api/send-email`;
      }
    }
  }
  return endpoint;
};

/**
 * Send a custom email
 * @param {Object} options - Email options
 * @param {string|string[]} options.to - Recipient email address(es)
 * @param {string} options.subject - Email subject
 * @param {string} options.body - Plain text body
 * @param {string} [options.html] - HTML body (optional, overrides body)
 * @param {string} [options.from] - Sender name (optional)
 * @param {string|string[]} [options.cc] - CC recipients (optional)
 * @param {string|string[]} [options.bcc] - BCC recipients (optional)
 * @returns {Promise<{success: boolean, messageId?: string, error?: string}>}
 */
export const sendEmail = async (options) => {
  const { to, subject, body, html, from, cc, bcc } = options;

  // Validate required fields
  if (!to || !subject || (!body && !html)) {
    console.warn('📧 sendEmail: missing required fields', {
      hasTo: !!to,
      hasSubject: !!subject,
      hasBody: !!body,
      hasHtml: !!html,
    });
    return {
      success: false,
      error: 'Missing required fields: to, subject, and body/html are required',
    };
  }

  try {
    const endpoint = getResolvedEmailApiUrl();

    const toList = Array.isArray(to) ? to : [to];
    console.groupCollapsed(`📧 Email request: "${subject}" → ${toList.join(', ')}`);
    console.log('endpoint:', endpoint);
    console.log('to:', to);
    console.log('cc:', cc);
    console.log('bcc:', bcc);
    console.log('from:', from);
    console.log('hasHtml:', !!html, 'hasBody:', !!body);
    console.groupEnd();
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to,
        subject,
        body,
        html,
        from,
        cc,
        bcc,
      }),
    });

    console.log('📧 Email API response:', response.status, response.statusText, 'endpoint:', endpoint);

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        const errorText = await response.text().catch(() => '');
        errorData = { error: `HTTP ${response.status}: ${response.statusText}`, errorText };
      }
      console.error('❌ Email send failed:', errorData);
      return {
        success: false,
        error: errorData.error || `Failed to send email: ${response.status} ${response.statusText}`,
        endpoint,
      };
    }

    const data = await response.json().catch(async () => {
      const text = await response.text().catch(() => '');
      return { messageId: undefined, raw: text };
    });
    console.log('✅ Email sent successfully:', data.messageId || '(no messageId)', {
      endpoint,
      to,
      subject,
    });
    return {
      success: true,
      messageId: data.messageId,
      endpoint,
    };
  } catch (error) {
    console.error('❌ Email send error (exception):', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      endpoint: EMAIL_API_ENDPOINT
    });
    return {
      success: false,
      error: error.message || 'Failed to send email. Please check if the email server is running.',
      endpoint: getResolvedEmailApiUrl(),
    };
  }
};

/**
 * Send order confirmation email
 * @param {Object} options - Order details
 * @param {string} options.customerEmail - Customer email
 * @param {string} options.customerName - Customer name
 * @param {string} options.orderId - Order ID
 * @param {string} options.orderTotal - Order total amount
 * @param {Array} options.items - Order items
 * @param {string} [options.orderDate] - Order date (optional)
 * @returns {Promise<{success: boolean, messageId?: string, error?: string}>}
 */
export const sendOrderConfirmation = async (options) => {
  const {
    customerEmail,
    customerName,
    orderId,
    orderTotal,
    items,
    orderDate = new Date().toLocaleDateString(),
  } = options;

  const itemsList = items
    .map(
      (item) =>
        `<tr>
          <td style="padding: 10px; border-bottom: 1px solid #eee;">${item.name}</td>
          <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center;">${item.quantity}</td>
          <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">${item.price} OMR</td>
        </tr>`
    )
    .join('');

  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9f9f9;">
      <div style="background-color: #ffffff; padding: 30px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
        <h1 style="color: #4a90e2; margin-bottom: 20px;">Thank You for Your Order!</h1>
        
        <p style="color: #333; font-size: 16px; line-height: 1.6;">
          Dear ${customerName},
        </p>
        
        <p style="color: #333; font-size: 16px; line-height: 1.6;">
          Your order has been successfully placed and is being processed.
        </p>
        
        <div style="background-color: #f0f0f0; padding: 20px; border-radius: 5px; margin: 20px 0;">
          <p style="margin: 5px 0;"><strong>Order Number:</strong> #${orderId}</p>
          <p style="margin: 5px 0;"><strong>Order Date:</strong> ${orderDate}</p>
          <p style="margin: 5px 0;"><strong>Total:</strong> ${orderTotal} OMR</p>
        </div>
        
        <div style="background-color: #e8f4f8; padding: 15px; border-left: 4px solid #4a90e2; margin: 20px 0;">
          <p style="color: #333; font-size: 14px; margin: 0;">
            💳 <strong>Payment:</strong> Please complete payment to process your order. You will receive a payment link shortly.
          </p>
        </div>
        
        <h2 style="color: #4a90e2; font-size: 20px; margin-top: 30px;">Order Details</h2>
        
        <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
          <thead>
            <tr style="background-color: #f0f0f0;">
              <th style="padding: 10px; text-align: left; border-bottom: 2px solid #4a90e2;">Item</th>
              <th style="padding: 10px; text-align: center; border-bottom: 2px solid #4a90e2;">Quantity</th>
              <th style="padding: 10px; text-align: right; border-bottom: 2px solid #4a90e2;">Price</th>
            </tr>
          </thead>
          <tbody>
            ${itemsList}
          </tbody>
        </table>
        
        <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #eee;">
          <p style="color: #666; font-size: 14px; line-height: 1.6;">
            We'll send you another email once your order has been shipped.
          </p>
          <p style="color: #666; font-size: 14px; line-height: 1.6;">
            If you have any questions, please contact us at info@wasl.om
          </p>
        </div>
        
        <div style="margin-top: 30px; text-align: center;">
          <p style="color: #999; font-size: 12px;">
            © ${new Date().getFullYear()} WASL. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  `;

  return sendEmail({
    to: customerEmail,
    subject: `Order Confirmation - Order #${orderId}`,
    html,
    body: `Your order #${orderId} has been confirmed. Total: ${orderTotal} OMR`,
  });
};

/**
 * Send welcome email to new users
 * @param {Object} options - User details
 * @param {string} options.email - User email
 * @param {string} options.name - User name
 * @returns {Promise<{success: boolean, messageId?: string, error?: string}>}
 */
export const sendWelcomeEmail = async (options) => {
  const { email, name } = options;

  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9f9f9;">
      <div style="background-color: #ffffff; padding: 30px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
        <h1 style="color: #4a90e2; margin-bottom: 20px;">Welcome to WASL! 🎉</h1>
        
        <p style="color: #333; font-size: 16px; line-height: 1.6;">
          Dear ${name},
        </p>
        
        <p style="color: #333; font-size: 16px; line-height: 1.6;">
          Thank you for joining WASL! We're excited to have you as part of our community.
        </p>
        
        <div style="background-color: #e8f4f8; padding: 20px; border-left: 4px solid #4a90e2; margin: 20px 0;">
          <p style="color: #333; font-size: 14px; margin: 0;">
            Start exploring our wide range of products and enjoy a seamless shopping experience!
          </p>
        </div>
        
        <div style="text-align: center; margin: 30px 0;">
          <a href="https://wasl.om" 
             style="display: inline-block; padding: 15px 30px; background-color: #4a90e2; color: white; text-decoration: none; border-radius: 5px; font-weight: bold;">
            Start Shopping
          </a>
        </div>
        
        <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #eee;">
          <p style="color: #666; font-size: 14px; line-height: 1.6;">
            If you have any questions, our customer support team is here to help.
          </p>
          <p style="color: #666; font-size: 14px; line-height: 1.6;">
            Contact us at: info@wasl.om
          </p>
        </div>
        
        <div style="margin-top: 30px; text-align: center;">
          <p style="color: #999; font-size: 12px;">
            © ${new Date().getFullYear()} WASL. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  `;

  return sendEmail({
    to: email,
    subject: 'Welcome to WASL - Start Your Shopping Journey!',
    html,
    body: `Welcome to WASL, ${name}! We're excited to have you.`,
  });
};

/**
 * Send password reset email
 * @param {Object} options - Reset details
 * @param {string} options.email - User email
 * @param {string} options.name - User name
 * @param {string} options.resetLink - Password reset link
 * @returns {Promise<{success: boolean, messageId?: string, error?: string}>}
 */
export const sendPasswordResetEmail = async (options) => {
  const { email, name, resetLink } = options;

  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9f9f9;">
      <div style="background-color: #ffffff; padding: 30px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
        <h1 style="color: #4a90e2; margin-bottom: 20px;">Reset Your Password</h1>
        
        <p style="color: #333; font-size: 16px; line-height: 1.6;">
          Dear ${name},
        </p>
        
        <p style="color: #333; font-size: 16px; line-height: 1.6;">
          We received a request to reset your password. Click the button below to create a new password:
        </p>
        
        <div style="text-align: center; margin: 30px 0;">
          <a href="${resetLink}" 
             style="display: inline-block; padding: 15px 30px; background-color: #4a90e2; color: white; text-decoration: none; border-radius: 5px; font-weight: bold;">
            Reset Password
          </a>
        </div>
        
        <div style="background-color: #fff3cd; padding: 15px; border-left: 4px solid #ffc107; margin: 20px 0;">
          <p style="color: #856404; font-size: 14px; margin: 0;">
            ⚠️ This link will expire in 1 hour. If you didn't request this, please ignore this email.
          </p>
        </div>
        
        <p style="color: #666; font-size: 14px; line-height: 1.6; margin-top: 20px;">
          If the button doesn't work, copy and paste this link into your browser:
        </p>
        <p style="color: #4a90e2; font-size: 12px; word-break: break-all;">
          ${resetLink}
        </p>
        
        <div style="margin-top: 30px; text-align: center;">
          <p style="color: #999; font-size: 12px;">
            © ${new Date().getFullYear()} WASL. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  `;

  return sendEmail({
    to: email,
    subject: 'Reset Your WASL Password',
    html,
    body: `Reset your password using this link: ${resetLink}`,
  });
};

/**
 * Send password reset link via Node email server (Supabase recovery URL + Gmail SMTP).
 * @param {Object} options
 * @param {string} options.email
 * @param {string} options.redirectTo
 * @returns {Promise<{success: boolean, messageId?: string, error?: string}>}
 */
export const sendPasswordRecoveryLink = async (options) => {
  const { email, redirectTo } = options || {};

  if (!email) {
    return { success: false, error: 'Email is required' };
  }

  try {
    let endpoint = getResolvedEmailApiUrl().replace('/send-email', '/forgot-password/send-link');
    if (!endpoint.endsWith('/forgot-password/send-link')) {
      endpoint = 'http://localhost:3001/api/forgot-password/send-link';
      if (typeof window !== 'undefined') {
        const isProduction =
          window.location.hostname === 'wasl.om' || window.location.hostname.includes('wasl.om');
        const hostname = window.location.hostname;
        const protocol = window.location.protocol;
        const isPrivateNetworkHost =
          hostname.startsWith('192.168.') ||
          hostname.startsWith('10.') ||
          /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname);
        if (isProduction && endpoint.includes('localhost')) {
          endpoint = 'https://wasl.om/api/email/forgot-password/send-link';
        }
        // Local LAN testing: point to the same host running the email server on :3001.
        if (isPrivateNetworkHost && endpoint.includes('localhost:3001')) {
          endpoint = `${protocol}//${hostname}:3001/api/forgot-password/send-link`;
        }
      }
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, redirectTo }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || data.success === false) {
      return {
        success: false,
        error: data.error || `Email API error: ${response.status} ${response.statusText}`,
      };
    }

    return {
      success: true,
      messageId: data.messageId,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to send password recovery email',
    };
  }
};

/**
 * Send contact form submission notification
 * @param {Object} options - Contact form details
 * @param {string} options.name - Sender name
 * @param {string} options.email - Sender email
 * @param {string} options.phone - Sender phone (optional)
 * @param {string} options.message - Message content
 * @param {string} options.subject - Message subject (optional)
 * @returns {Promise<{success: boolean, messageId?: string, error?: string}>}
 */
export const sendContactFormNotification = async (options) => {
  const { name, email, phone, message, subject = 'Contact Form Submission' } = options;

  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9f9f9;">
      <div style="background-color: #ffffff; padding: 30px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
        <h1 style="color: #4a90e2; margin-bottom: 20px;">New Contact Form Submission</h1>
        
        <div style="background-color: #f0f0f0; padding: 20px; border-radius: 5px; margin: 20px 0;">
          <p style="margin: 10px 0;"><strong>From:</strong> ${name}</p>
          <p style="margin: 10px 0;"><strong>Email:</strong> ${email}</p>
          ${phone ? `<p style="margin: 10px 0;"><strong>Phone:</strong> ${phone}</p>` : ''}
          <p style="margin: 10px 0;"><strong>Subject:</strong> ${subject}</p>
        </div>
        
        <div style="margin: 20px 0;">
          <h3 style="color: #333; font-size: 16px;">Message:</h3>
          <p style="color: #666; font-size: 14px; line-height: 1.6; white-space: pre-wrap;">
${message}
          </p>
        </div>
        
        <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #eee;">
          <p style="color: #999; font-size: 12px;">
            Submitted on: ${new Date().toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  `;

  return sendEmail({
    to: 'info@wasl.om', // Send to WASL admin email
    subject: `Contact Form: ${subject}`,
    html,
    body: `New contact form submission from ${name} (${email}): ${message}`,
  });
};

/**
 * Send shipping notification email
 * @param {Object} options - Shipping details
 * @param {string} options.customerEmail - Customer email
 * @param {string} options.customerName - Customer name
 * @param {string} options.orderId - Order ID
 * @param {string} options.trackingNumber - Tracking number
 * @param {string} options.carrier - Shipping carrier
 * @returns {Promise<{success: boolean, messageId?: string, error?: string}>}
 */
export const sendShippingNotification = async (options) => {
  const { customerEmail, customerName, orderId, trackingNumber, carrier } = options;

  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9f9f9;">
      <div style="background-color: #ffffff; padding: 30px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
        <h1 style="color: #4a90e2; margin-bottom: 20px;">Your Order Has Been Shipped! 📦</h1>
        
        <p style="color: #333; font-size: 16px; line-height: 1.6;">
          Dear ${customerName},
        </p>
        
        <p style="color: #333; font-size: 16px; line-height: 1.6;">
          Great news! Your order has been shipped and is on its way to you.
        </p>
        
        <div style="background-color: #f0f0f0; padding: 20px; border-radius: 5px; margin: 20px 0;">
          <p style="margin: 10px 0;"><strong>Order Number:</strong> #${orderId}</p>
          <p style="margin: 10px 0;"><strong>Tracking Number:</strong> ${trackingNumber}</p>
          <p style="margin: 10px 0;"><strong>Carrier:</strong> ${carrier}</p>
        </div>
        
        <div style="background-color: #e8f4f8; padding: 20px; border-left: 4px solid #4a90e2; margin: 20px 0;">
          <p style="color: #333; font-size: 14px; margin: 0;">
            You can track your shipment using the tracking number above. Estimated delivery is 2-5 business days.
          </p>
        </div>
        
        <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #eee;">
          <p style="color: #666; font-size: 14px; line-height: 1.6;">
            If you have any questions about your order, please contact us at info@wasl.om
          </p>
        </div>
        
        <div style="margin-top: 30px; text-align: center;">
          <p style="color: #999; font-size: 12px;">
            © ${new Date().getFullYear()} WASL. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  `;

  return sendEmail({
    to: customerEmail,
    subject: `Your Order Has Been Shipped - Order #${orderId}`,
    html,
    body: `Your order #${orderId} has been shipped. Tracking number: ${trackingNumber}`,
  });
};

/**
 * Send new order notification to store owner
 * @param {Object} options - Order details
 * @param {string} options.storeEmail - Store owner email
 * @param {string} options.storeName - Store/brand name
 * @param {string} options.orderId - Order ID
 * @param {string} options.customerName - Customer name
 * @param {string} options.customerEmail - Customer email
 * @param {string} options.orderTotal - Order total amount
 * @param {Array} options.items - Order items
 * @param {Object} options.shippingAddress - Shipping address object
 * @param {string} [options.orderDate] - Order date (optional)
 * @returns {Promise<{success: boolean, messageId?: string, error?: string}>}
 */
export const sendNewOrderNotificationToStore = async (options) => {
  const {
    storeEmail,
    storeName,
    orderId,
    customerName,
    customerEmail,
    orderTotal,
    items,
    shippingAddress,
    orderDate = new Date().toLocaleDateString(),
  } = options;

  console.log('📧 sendNewOrderNotificationToStore called with:', {
    storeEmail,
    storeName,
    orderId,
    itemsCount: items?.length || 0
  });

  // Validate required fields
  if (!storeEmail || !storeEmail.includes('@')) {
    console.error('❌ Invalid store email:', storeEmail);
    return {
      success: false,
      error: `Invalid store email: ${storeEmail}`,
    };
  }

  if (!items || items.length === 0) {
    console.error('❌ No items provided for store email');
    return {
      success: false,
      error: 'No items provided for store email',
    };
  }

  const itemsList = items
    .map(
      (item) => {
        // Ensure price is a number before formatting - handle all edge cases
        let price = 0;
        if (typeof item.price === 'number') {
          price = item.price;
        } else if (typeof item.price === 'string') {
          const parsed = parseFloat(item.price);
          price = isNaN(parsed) ? 0 : parsed;
        } else {
          price = 0;
        }
        
        // Ensure it's a valid number with toFixed method
        const formattedPrice = typeof price === 'number' && !isNaN(price) ? price.toFixed(2) : '0.00';
        
        return `<tr>
          <td style="padding: 10px; border-bottom: 1px solid #eee;">${item.name || 'Unknown Product'}</td>
          <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center;">${item.quantity || 1}</td>
          <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">${formattedPrice} OMR</td>
        </tr>`;
      }
    )
    .join('');

  const shippingDetails = shippingAddress
    ? `
      <div style="background-color: #f0f0f0; padding: 20px; border-radius: 5px; margin: 20px 0;">
        <h3 style="color: #4a90e2; font-size: 16px; margin-top: 0;">Shipping Address</h3>
        <p style="margin: 5px 0; color: #333;">${shippingAddress.full_name || customerName}</p>
        ${shippingAddress.phone_number ? `<p style="margin: 5px 0; color: #333;">📱 ${shippingAddress.phone_number}</p>` : ''}
        ${shippingAddress.email ? `<p style="margin: 5px 0; color: #333;">📧 ${shippingAddress.email}</p>` : ''}
        ${shippingAddress.area || shippingAddress.city || shippingAddress.governorate
          ? `<p style="margin: 5px 0; color: #333;">📍 ${[
              shippingAddress.area,
              shippingAddress.city,
              shippingAddress.governorate,
              shippingAddress.country || 'Oman'
            ].filter(Boolean).join(', ')}</p>` : ''}
        ${shippingAddress.delivery_notes ? `<p style="margin: 10px 0 0 0; padding-top: 10px; border-top: 1px solid #ddd; color: #666; font-style: italic;">Note: ${shippingAddress.delivery_notes}</p>` : ''}
      </div>
    `
    : '';

  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9f9f9;">
      <div style="background-color: #ffffff; padding: 30px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
        <h1 style="color: #4a90e2; margin-bottom: 20px;">🛒 New Order Received!</h1>
        
        <p style="color: #333; font-size: 16px; line-height: 1.6;">
          Dear ${storeName},
        </p>
        
        <p style="color: #333; font-size: 16px; line-height: 1.6;">
          You have received a new order that requires your attention.
        </p>
        
        <div style="background-color: #fff3cd; padding: 15px; border-left: 4px solid #ffc107; margin: 20px 0;">
          <p style="color: #856404; font-size: 14px; margin: 0; font-weight: 600;">
            ⚠️ Please process this order in the admin panel as soon as possible.
          </p>
        </div>
        
        <div style="background-color: #f0f0f0; padding: 20px; border-radius: 5px; margin: 20px 0;">
          <p style="margin: 5px 0;"><strong>Order Number:</strong> #${orderId}</p>
          <p style="margin: 5px 0;"><strong>Order Date:</strong> ${orderDate}</p>
          <p style="margin: 5px 0;"><strong>Customer:</strong> ${customerName}</p>
          <p style="margin: 5px 0;"><strong>Customer Email:</strong> ${customerEmail}</p>
          <p style="margin: 5px 0;"><strong>Order Total:</strong> ${orderTotal} OMR</p>
        </div>
        
        <h2 style="color: #4a90e2; font-size: 20px; margin-top: 30px;">Order Items</h2>
        
        <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
          <thead>
            <tr style="background-color: #f0f0f0;">
              <th style="padding: 10px; text-align: left; border-bottom: 2px solid #4a90e2;">Item</th>
              <th style="padding: 10px; text-align: center; border-bottom: 2px solid #4a90e2;">Quantity</th>
              <th style="padding: 10px; text-align: right; border-bottom: 2px solid #4a90e2;">Price</th>
            </tr>
          </thead>
          <tbody>
            ${itemsList}
          </tbody>
        </table>
        
        ${shippingDetails}
        
        <div style="background-color: #e8f4f8; padding: 20px; border-left: 4px solid #4a90e2; margin: 20px 0;">
          <p style="color: #333; font-size: 14px; margin: 0;">
            📋 <strong>Next Steps:</strong> Please log into your admin panel to process this order and create the shipment.
          </p>
        </div>
        
        <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #eee;">
          <p style="color: #666; font-size: 14px; line-height: 1.6;">
            For any questions or support, contact WASL at info@wasl.om
          </p>
        </div>
        
        <div style="margin-top: 30px; text-align: center;">
          <p style="color: #999; font-size: 12px;">
            © ${new Date().getFullYear()} WASL. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  `;

  console.log('📧 Calling sendEmail with:', {
    to: storeEmail,
    subject: `New Order #${orderId} - ${customerName}`,
    endpoint: EMAIL_API_ENDPOINT
  });

  const emailResult = await sendEmail({
    to: storeEmail,
    subject: `New Order #${orderId} - ${customerName}`,
    html,
    body: `New order received: Order #${orderId} from ${customerName} (${customerEmail}). Total: ${orderTotal} OMR`,
    from: 'WASL'
  });

  console.log('📧 sendEmail result:', emailResult);
  return emailResult;
};

export default {
  sendEmail,
  sendOrderConfirmation,
  sendWelcomeEmail,
  sendPasswordResetEmail,
  sendPasswordRecoveryLink,
  sendContactFormNotification,
  sendShippingNotification,
  sendNewOrderNotificationToStore,
};

